import caseInsensitiveCompare from '@liveauctioneers/caterwaul-components/lib/utils/caseInsensitiveCompare';
import { createSelector } from 'reselect';
import { GlobalState } from '../rootReducer';
import UAParser from 'ua-parser-js';

export type State = {
    deviceType: string;
    isOutdated: boolean;
    majorVersion: number;
    microsoftBrowser: boolean;
    mobile: string;
    name: string;
    os: string;
    osVersion: string;
    userAgentString: string;
};

export const DEFAULT_STATE: State = {
    deviceType: 'computer',
    isOutdated: false,
    majorVersion: 0,
    microsoftBrowser: false,
    mobile: '',
    name: '',
    os: '',
    osVersion: '',
    userAgentString: '',
};

export default function reducer(state: State = DEFAULT_STATE, action: any = {}): State {
    switch (action.type) {
        default:
            return state;
    }
}

/* SELECTORS */
const stateSelector = (state: GlobalState) => state.browser;

export const getUiBrowser = createSelector(stateSelector, (state) => state);

export const isUiMobile = createSelector(getUiBrowser, (uiBrowser) => uiBrowser.mobile);

export const isBrowserIE = createSelector(getUiBrowser, (uiBrowser) => {
    return Boolean(uiBrowser.name && caseInsensitiveCompare(uiBrowser.name, 'ie'));
});

export const getMobileBrowserOS = createSelector(getUiBrowser, (uiBrowser) => {
    // @ts-ignore
    return uiBrowser.mobile ? uiBrowser.os : '';
});

export const isUiMicrosoftBrowser = createSelector(getUiBrowser, (uiBrowser) => uiBrowser.microsoftBrowser);

export const isUiTablet = createSelector(isUiMobile, (mobile) => {
    return mobile === 'tablet';
});

export const getUserAgent = createSelector(stateSelector, (state) => state.userAgentString);

export const getBrowserDataFromUA = (userAgentString: string) => {
    const parser = new UAParser();

    parser.setUA(userAgentString);
    const result = parser.getResult();

    let name = '';
    let os = '';
    let osVersion = '';
    let majorVersion = 0;
    let mobile = '';
    let deviceType = '';

    if (result?.browser) {
        if (typeof result.browser.name === 'string') {
            // Googlebot don't have a name
            name = result.browser.name.toLowerCase();
        }
        if (result.browser.major) {
            majorVersion = parseInt(result.browser.major, 10);
        }
    }
    if (result?.os) {
        if (typeof result.os.name === 'string') {
            os = result.os.name.toLowerCase();
        }
        if (typeof result.os.version === 'string') {
            osVersion = result.os.version;
        }
    }
    if (result?.device) {
        deviceType = result.device.type || 'computer'; // [console, mobile, tablet, smarttv, wearable, embedded]

        if (result.device.type === 'mobile') {
            mobile = 'phone';
        } else if (result.device.type === 'tablet') {
            mobile = result.device.type;
        }
    }

    const minimumBrowserVersions = {
        chrome: 45,
        edge: 13,
        firefox: 41,
        ie: 12,
        safari: 9,
    };

    let isOutdated = false;
    if (Object.prototype.hasOwnProperty.call(minimumBrowserVersions, name)) {
        isOutdated = minimumBrowserVersions[name] > majorVersion;
    }

    const isGoogle = userAgentString.toLowerCase().includes('google');
    if (isGoogle) {
        isOutdated = false;
    }

    return {
        deviceType,
        isOutdated,
        majorVersion,
        microsoftBrowser: name === 'ie' || name === 'edge',
        mobile,
        name,
        os,
        osVersion,
    };
};
